import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Select,
  Button,
  Row,
  Col,
  Card,
  Progress,
  List,
  Tag,
  Avatar,
  Empty,
  Spin,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Tooltip,
  Dropdown,
  Menu,
  message,
} from 'antd';
import {
  CalendarOutlined,
  ExclamationCircleOutlined,
  LeftOutlined,
  RightOutlined,
  DownOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import NewTask from '@iso/containers/Pages/Task/NewTask';
import { operationsPlanService, propertyService, assetService } from '@iso/services';
import {
  PLAN_CATEGORIES,
  PLAN_ITEM_STATUSES,
  PLAN_ITEM_STATUS_MAP,
} from '@iso/constants/operationsPlanner';
import { StatCard, SectionCard, EmptyBox, OPS_COLORS, initials } from './OperationsUI';

const { Option } = Select;
const { TextArea } = Input;

const MONTHS = moment.monthsShort(); // ['Jan', 'Feb', ...]
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Each plan category has its own colour: a strong `dot` (headers, legends), a
// `bar` fill for the timeline Gantt (plus `barAlt` for the in-progress hatch),
// and a light chip triplet (bg/border/text) for calendar tiles.
const CATEGORY_COLORS = {
  Production: { dot: '#10b981', bar: '#6ee7b7', barAlt: '#34d399', chipBg: '#ecfdf5', chipBorder: '#a7f3d0', chipText: '#065f46' },
  'Animal Health & Welfare': { dot: '#f59e0b', bar: '#fbbf24', barAlt: '#f59e0b', chipBg: '#fef3c7', chipBorder: '#fde68a', chipText: '#92400e' },
  'Crop & Plant Health': { dot: '#22c55e', bar: '#86efac', barAlt: '#4ade80', chipBg: '#f0fdf4', chipBorder: '#bbf7d0', chipText: '#166534' },
  'Land, Soil & Water': { dot: '#0891b2', bar: '#67e8f9', barAlt: '#22d3ee', chipBg: '#ecfeff', chipBorder: '#a5f3fc', chipText: '#155e75' },
  Biosecurity: { dot: '#a855f7', bar: '#d8b4fe', barAlt: '#c084fc', chipBg: '#faf5ff', chipBorder: '#e9d5ff', chipText: '#6b21a8' },
  'Machinery & Equipment': { dot: '#64748b', bar: '#cbd5e1', barAlt: '#94a3b8', chipBg: '#f8fafc', chipBorder: '#e2e8f0', chipText: '#334155' },
  Infrastructure: { dot: '#71717a', bar: '#d4d4d8', barAlt: '#a1a1aa', chipBg: '#fafafa', chipBorder: '#e4e4e7', chipText: '#3f3f46' },
  Workforce: { dot: '#6366f1', bar: '#a5b4fc', barAlt: '#818cf8', chipBg: '#eef2ff', chipBorder: '#c7d2fe', chipText: '#3730a3' },
  Safety: { dot: '#f97316', bar: '#fdba74', barAlt: '#fb923c', chipBg: '#fff7ed', chipBorder: '#fed7aa', chipText: '#c2410c' },
  Compliance: { dot: '#ef4444', bar: '#fca5a5', barAlt: '#f87171', chipBg: '#fef2f2', chipBorder: '#fecaca', chipText: '#b91c1c' },
  Environment: { dot: '#14b8a6', bar: '#5eead4', barAlt: '#2dd4bf', chipBg: '#f0fdfa', chipBorder: '#99f6e4', chipText: '#115e59' },
  'Harvest, Quality & Logistics': { dot: '#eab308', bar: '#fde047', barAlt: '#eab308', chipBg: '#fefce8', chipBorder: '#fef08a', chipText: '#854d0e' },
};
const categoryColor = (c) => CATEGORY_COLORS[c] || CATEGORY_COLORS.Infrastructure;
// Overdue tiles are always red, whatever the category.
const OVERDUE_CHIP = { chipBg: '#fef2f2', chipBorder: '#fca5a5', chipText: '#b91c1c', dot: '#ef4444' };

// Recurrence options for seasonal plan items. Each maps to a moment step used to
// walk occurrence dates across the plan year.
const REPEAT_OPTIONS = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'weekly', label: 'Weekly', step: [1, 'weeks'] },
  { value: 'fortnightly', label: 'Fortnightly', step: [2, 'weeks'] },
  { value: 'monthly', label: 'Monthly', step: [1, 'months'] },
  { value: 'quarterly', label: 'Quarterly', step: [3, 'months'] },
  { value: 'half_yearly', label: 'Half-yearly', step: [6, 'months'] },
  { value: 'custom', label: 'Custom…' },
];
const REPEAT_MAP = REPEAT_OPTIONS.reduce((m, o) => {
  m[o.value] = o;
  return m;
}, {});
const CUSTOM_UNITS = [
  { value: 'days', label: 'days' },
  { value: 'weeks', label: 'weeks' },
  { value: 'months', label: 'months' },
];
const MAX_SERIES = 366;

// The [n, unit] step for a repeat selection. 'custom' takes the interval/unit
// the user typed (e.g. every 10 days); the presets carry their own step.
const stepFor = (repeat, interval, unit) => {
  if (repeat === 'custom') {
    const n = Math.max(1, parseInt(interval, 10) || 1);
    return [n, CUSTOM_UNITS.some((u) => u.value === unit) ? unit : 'days'];
  }
  const opt = REPEAT_MAP[repeat];
  return opt && opt.step ? opt.step : null;
};

// Expand a recurrence into occurrence dates (YYYY-MM-DD) from a start date.
// Weekly lands on the same weekday each week; monthly on the same date each
// month. endMode 'year' runs through 31 Dec of the plan year; 'count' gives N.
const expandOccurrences = (start, step, endMode, count, planYear) => {
  if (!start || !step) return start ? [start.format('YYYY-MM-DD')] : [];
  const [n, unit] = step;
  const cap = endMode === 'count' ? Math.max(1, Math.min(MAX_SERIES, count || 1)) : MAX_SERIES;
  const endOfYear = moment({ year: planYear }).endOf('year');
  const out = [];
  for (let i = 0; out.length < cap && i < MAX_SERIES; i += 1) {
    // Anchor each occurrence to the start date (start + i×step) rather than
    // stepping off the previous one: a month-end start would otherwise drift
    // (31 Jan -> 28 Feb -> 28 Mar, instead of 31 Mar).
    const d = start.clone().add(i * n, unit);
    if (endMode === 'year' && d.isAfter(endOfYear, 'day')) break;
    out.push(d.format('YYYY-MM-DD'));
  }
  return out.length ? out : [start.format('YYYY-MM-DD')];
};

// Advanced (maintenance-style) plan-item field options.
const ENTERPRISES = ['Horticulture', 'Livestock', 'Cropping', 'Dairy', 'Viticulture', 'Mixed', 'Other'];
const TASK_TYPES = [
  'Preventative maintenance',
  'Corrective maintenance',
  'Inspection',
  'Compliance',
  'Operational',
  'Other',
];
const RISK_LEVELS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];
const catColor = (p) =>
  p >= 67 ? OPS_COLORS.green : p >= 34 ? OPS_COLORS.orange : OPS_COLORS.red;

const statusTag = (status, isOverdue) => {
  if (isOverdue) {
    return <Tag color="red">Overdue</Tag>;
  }
  const meta = PLAN_ITEM_STATUS_MAP[status];
  return <Tag color={meta ? meta.color : 'default'}>{meta ? meta.label : status}</Tag>;
};

// Fill style for a timeline bar given the category and the month's item(s):
// completed → solid strong colour, in-progress → diagonal hatch, overdue → red
// inset outline, otherwise the flat category fill.
const barFill = (cat, monthItems) => {
  const c = categoryColor(cat);
  const anyOverdue = monthItems.some((i) => i.is_overdue);
  const allCompleted = monthItems.length > 0 && monthItems.every((i) => i.status === 'completed');
  const anyInProgress = monthItems.some((i) => i.status === 'in_progress');
  const style = { background: c.bar };
  if (allCompleted) {
    style.background = c.dot;
  } else if (anyInProgress) {
    style.backgroundImage = `repeating-linear-gradient(45deg, ${c.bar} 0, ${c.bar} 5px, ${c.barAlt} 5px, ${c.barAlt} 10px)`;
  }
  if (anyOverdue) {
    style.boxShadow = 'inset 0 0 0 1.5px #ef4444';
  }
  return style;
};

const Planner = () => {
  const activePropertyId = useSelector((state) => state.property.activePropertyId);

  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Pre-filled "New Task" form launched from a plan item.
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [taskPrefill, setTaskPrefill] = useState(null);
  const [taskAreas, setTaskAreas] = useState([]);
  const [calValue, setCalValue] = useState(moment());
  // Recurrence UI state for the New Plan Item modal.
  const [repeat, setRepeat] = useState('none');
  const [repeatEnd, setRepeatEnd] = useState('year');
  // Advanced (maintenance-style) fields: the expand toggle and the linked-record
  // option lists (areas / assets / users), loaded lazily.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [planAreas, setPlanAreas] = useState([]);
  const [planAssets, setPlanAssets] = useState([]);
  const [planUsers, setPlanUsers] = useState([]);
  // Timeline collapsed categories + calendar category filter.
  const [collapsed, setCollapsed] = useState({});
  const [categoryFilter, setCategoryFilter] = useState(() => new Set(PLAN_CATEGORIES));

  const [itemForm] = Form.useForm();
  const [planForm] = Form.useForm();

  const selectedPlan = useMemo(
    () => plans.find((p) => p.id === selectedPlanId) || null,
    [plans, selectedPlanId]
  );
  const planYear = selectedPlan?.year || moment().year();

  const loadPlans = () => {
    if (!activePropertyId) return;
    operationsPlanService
      .getPlans(activePropertyId)
      .then((res) => {
        const list = res?.plans || [];
        setPlans(list);
        // Keep the current selection if it still exists, otherwise pick the first.
        setSelectedPlanId((prev) =>
          list.some((p) => p.id === prev) ? prev : list[0]?.id || null
        );
      })
      .catch(() => message.error('Could not load plans'));
  };

  const loadDashboard = (planId) => {
    if (!planId) {
      setDashboard(null);
      setItems([]);
      return;
    }
    setLoading(true);
    Promise.all([
      operationsPlanService.getDashboard(planId),
      operationsPlanService.getItems(planId),
    ])
      .then(([dashRes, itemsRes]) => {
        setDashboard(dashRes?.data || null);
        setItems(itemsRes?.items || []);
      })
      .catch(() => message.error('Could not load plan details'))
      .finally(() => setLoading(false));
  };

  useEffect(loadPlans, [activePropertyId]);
  useEffect(() => loadDashboard(selectedPlanId), [selectedPlanId]);

  // Point the calendar at the plan's year when the plan changes (keep the
  // current month if we're already looking at that year).
  useEffect(() => {
    setCalValue((prev) => (prev.year() === planYear ? prev : moment({ year: planYear, month: 0, day: 1 })));
  }, [planYear]);

  // The linked-record dropdowns are keyed to a property. When the active
  // property changes, drop the cached lists (and the task-map areas) so
  // ensureAdvancedData refetches for the new property instead of offering the
  // previous property's areas/assets/users.
  useEffect(() => {
    setPlanAreas([]);
    setPlanAssets([]);
    setPlanUsers([]);
    setTaskAreas([]);
  }, [activePropertyId]);

  // Areas / assets / users for the Advanced form's linked-record dropdowns.
  // Fetched lazily on first modal open so the Planner doesn't pay for them until
  // someone actually opens the item form.
  const ensureAdvancedData = () => {
    if (!activePropertyId) return;
    if (!planAreas.length) {
      propertyService
        .viewProperty(activePropertyId)
        .then((res) => {
          if (res && res.code === '0000') setPlanAreas(res.primary_objects || []);
        })
        .catch(() => message.error('Could not load locations'));
    }
    if (!planAssets.length) {
      assetService
        .getList(activePropertyId)
        .then((res) => setPlanAssets(res?.facilities || []))
        .catch(() => message.error('Could not load assets'));
    }
    if (!planUsers.length) {
      propertyService
        .getListUsersOfProperty(activePropertyId)
        .then((res) => setPlanUsers(res?.users || []))
        .catch(() => message.error('Could not load people'));
    }
  };

  const openNewItem = () => {
    setEditingItem(null);
    itemForm.resetFields();
    itemForm.setFieldsValue({
      status: 'not_yet_due',
      repeat: 'none',
      repeatEnd: 'year',
      repeatCount: 12,
      repeatInterval: 10,
      repeatUnit: 'days',
    });
    setRepeat('none');
    setRepeatEnd('year');
    setAdvancedOpen(false);
    ensureAdvancedData();
    setItemModalOpen(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    // If the item belongs to a repeating series, edit the series: prefill the
    // Repeat controls from its stored config and anchor the date on the series
    // start (earliest occurrence), so changing the cadence regenerates correctly.
    const isSeries = !!item.series_id;
    const seriesStart = isSeries
      ? items
          .filter((i) => i.series_id === item.series_id && i.scheduled_date)
          .map((i) => i.scheduled_date)
          .sort()[0] || item.scheduled_date
      : item.scheduled_date;
    const rpt = isSeries ? item.repeat_type || 'none' : 'none';
    const rptEnd = item.repeat_end || 'year';
    setRepeat(rpt);
    setRepeatEnd(rptEnd);
    itemForm.setFieldsValue({
      title: item.title,
      category: item.category,
      status: item.status,
      scheduled_date: seriesStart ? moment(seriesStart) : null,
      notes: item.notes || '',
      repeat: rpt,
      repeatEnd: rptEnd,
      repeatCount: item.repeat_count || 12,
      repeatInterval: item.repeat_interval || 10,
      repeatUnit: item.repeat_unit || 'days',
      enterprise: item.enterprise || undefined,
      task_type: item.task_type || undefined,
      risk_level: item.risk_level || undefined,
      primary_object_id: item.primary_object_id || undefined,
      asset_id: item.asset_id || undefined,
      responsible_user_id: item.responsible_user_id || undefined,
      evidence_required: item.evidence_required || '',
    });
    const hasAdvanced =
      item.enterprise ||
      item.task_type ||
      item.risk_level ||
      item.primary_object_id ||
      item.asset_id ||
      item.responsible_user_id ||
      item.evidence_required;
    setAdvancedOpen(!!hasAdvanced);
    ensureAdvancedData();
    setItemModalOpen(true);
  };

  const handleSubmitItem = (values) => {
    const base = {
      plan_id: selectedPlanId,
      title: values.title,
      category: values.category,
      status: values.status || 'not_yet_due',
      notes: values.notes || null,
      // Advanced fields (null when the section wasn't used).
      enterprise: values.enterprise || null,
      task_type: values.task_type || null,
      risk_level: values.risk_level || null,
      primary_object_id: values.primary_object_id || null,
      asset_id: values.asset_id || null,
      responsible_user_id: values.responsible_user_id || null,
      evidence_required: values.evidence_required || null,
    };
    // Recurrence config persisted on every occurrence of the series.
    const repeatCfg = {
      repeat_type: values.repeat && values.repeat !== 'none' ? values.repeat : null,
      repeat_interval: values.repeat === 'custom' ? values.repeatInterval || null : null,
      repeat_unit: values.repeat === 'custom' ? values.repeatUnit || null : null,
      repeat_end: values.repeat && values.repeat !== 'none' ? values.repeatEnd : null,
      repeat_count: values.repeatEnd === 'count' ? values.repeatCount || null : null,
    };

    // New recurring item → expand into a dated series via the bulk endpoint.
    const recurring = !editingItem && values.repeat && values.repeat !== 'none';
    if (recurring) {
      if (!values.scheduled_date) {
        message.error('Pick a start date for a repeating item');
        return;
      }
      const dates = expandOccurrences(
        values.scheduled_date,
        stepFor(values.repeat, values.repeatInterval, values.repeatUnit),
        values.repeatEnd,
        values.repeatCount,
        planYear
      );
      setSubmitting(true);
      operationsPlanService
        .createItemsBulk({ ...base, ...repeatCfg, dates })
        .then((res) => {
          if (res && res.code === '0000') {
            message.success(`${res.created || dates.length} plan items added`);
            setItemModalOpen(false);
            itemForm.resetFields();
            setRepeat('none');
            loadDashboard(selectedPlanId);
          } else {
            message.error('Could not add the plan item series');
          }
        })
        .catch(() => message.error('Could not add the plan item series'))
        .finally(() => setSubmitting(false));
      return;
    }

    // Editing an existing series (or turning a one-off into one) → sync shared
    // fields and regenerate the upcoming occurrences via update-series.
    const seriesEdit = editingItem && (editingItem.series_id || (values.repeat && values.repeat !== 'none'));
    if (seriesEdit) {
      const dates =
        values.repeat && values.repeat !== 'none' && values.scheduled_date
          ? expandOccurrences(
              values.scheduled_date,
              stepFor(values.repeat, values.repeatInterval, values.repeatUnit),
              values.repeatEnd,
              values.repeatCount,
              planYear
            )
          : [];
      setSubmitting(true);
      operationsPlanService
        .updateSeries(editingItem.id, { ...base, ...repeatCfg, dates })
        .then((res) => {
          if (res && res.code === '0000') {
            message.success('Series updated');
            setItemModalOpen(false);
            setEditingItem(null);
            itemForm.resetFields();
            setRepeat('none');
            loadDashboard(selectedPlanId);
          } else {
            message.error('Could not update the series');
          }
        })
        .catch(() => message.error('Could not update the series'))
        .finally(() => setSubmitting(false));
      return;
    }

    setSubmitting(true);
    const payload = {
      ...base,
      scheduled_date: values.scheduled_date
        ? values.scheduled_date.format('YYYY-MM-DD')
        : null,
    };
    const req = editingItem
      ? operationsPlanService.updateItem(editingItem.id, payload)
      : operationsPlanService.createItem(payload);
    req
      .then((res) => {
        if (res && res.code === '0000') {
          message.success(editingItem ? 'Plan item updated' : 'Plan item added');
          setItemModalOpen(false);
          setEditingItem(null);
          itemForm.resetFields();
          loadDashboard(selectedPlanId);
        } else {
          message.error(editingItem ? 'Could not update plan item' : 'Could not add plan item');
        }
      })
      .catch(() => message.error('Could not save plan item'))
      .finally(() => setSubmitting(false));
  };

  const handleDeleteItem = () => {
    if (!editingItem) return;
    setSubmitting(true);
    operationsPlanService
      .deleteItem(editingItem.id)
      .then((res) => {
        if (res && res.code === '0000') {
          message.success('Plan item removed');
          setItemModalOpen(false);
          setEditingItem(null);
          loadDashboard(selectedPlanId);
        } else {
          message.error('Could not remove plan item');
        }
      })
      .catch(() => message.error('Could not remove plan item'))
      .finally(() => setSubmitting(false));
  };

  // Open the real "New Task" form pre-filled from a plan item, so the user
  // reviews and creates the task themselves (no auto-create).
  const PLAN_TO_TASK_STATUS = { completed: 'completed', in_progress: 'underway' };

  const openTaskForm = (item) => {
    if (!item) return;
    const details = `${item.notes ? `${item.notes}\n\n` : ''}From Operations Plan: ${
      selectedPlan?.name || ''
    } · ${item.category}`.trim();
    setTaskPrefill({
      title: item.title,
      details,
      due_date: item.scheduled_date ? moment(item.scheduled_date) : null,
      priority: 'medium',
      status: PLAN_TO_TASK_STATUS[item.status] || 'todo',
    });
    setItemModalOpen(false);
    setTaskFormOpen(true);
    // The task form's Area picker (and its map centring) needs the property's
    // primary objects. Fetched lazily on first open so the Planner doesn't pay
    // for it on every page load.
    if (activePropertyId && !taskAreas.length) {
      propertyService
        .viewProperty(activePropertyId)
        .then((res) => {
          if (res && res.code === '0000') setTaskAreas(res.primary_objects || []);
        })
        .catch(() => {});
    }
  };

  const handleCreatePlan = (values) => {
    setSubmitting(true);
    operationsPlanService
      .createPlan({
        property_id: activePropertyId,
        name: values.name,
        year: values.year,
        status: 'active',
      })
      .then((res) => {
        if (res && res.code === '0000') {
          message.success('Plan created');
          setPlanModalOpen(false);
          planForm.resetFields();
          const newId = res.plan?.id;
          operationsPlanService
            .getPlans(activePropertyId)
            .then((r) => {
              setPlans(r?.plans || []);
              if (newId) setSelectedPlanId(newId);
            })
            .catch(() => {});
        } else {
          message.error('Could not create plan');
        }
      })
      .catch(() => message.error('Could not create plan'))
      .finally(() => setSubmitting(false));
  };

  const noPlanEmpty = (
    <Empty description="No plans yet" style={{ marginTop: 48 }}>
      <Button type="primary" onClick={() => setPlanModalOpen(true)}>
        Create a plan
      </Button>
    </Empty>
  );

  const renderDashboardTab = () => {
    if (!selectedPlanId) return noPlanEmpty;

    const thisMonth = dashboard?.this_month || {};
    const overdue = dashboard?.overdue || {};
    const byCategory = dashboard?.completion_by_category || [];
    const upcoming = dashboard?.upcoming_activities || [];

    return (
      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card
              bordered={false}
              bodyStyle={{ padding: 20 }}
              style={{ height: '100%', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
            >
              <div style={{ color: OPS_COLORS.grey, fontSize: 13, marginBottom: 8 }}>Plan Completion</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Progress
                  type="circle"
                  width={64}
                  percent={dashboard?.plan_completion || 0}
                  strokeColor={OPS_COLORS.green}
                />
                <div style={{ color: OPS_COLORS.grey }}>{dashboard?.plan?.name}</div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard
              label="This Month"
              value={thisMonth.total || 0}
              sub={`${thisMonth.completed || 0} completed, ${thisMonth.in_progress || 0} in progress`}
              icon={<CalendarOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard
              label="Overdue"
              value={<span style={{ color: OPS_COLORS.red }}>{overdue.count || 0}</span>}
              sub={(overdue.titles || []).join(', ')}
              icon={<ExclamationCircleOutlined />}
              accent={overdue.count ? OPS_COLORS.red : undefined}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard
              label="Next 30 Days"
              value={dashboard?.next_30_days || 0}
              sub="Upcoming activities"
              icon={<CalendarOutlined />}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={14}>
            <SectionCard title="Completion by Category">
              {byCategory.length === 0 ? (
                <EmptyBox text="No plan items yet" />
              ) : (
                <div style={{ paddingTop: 8 }}>
                  {byCategory.map((cat) => (
                    <div key={cat.category} style={{ marginBottom: 16 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: 4,
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>{cat.category}</span>
                        <span style={{ fontWeight: 600 }}>{cat.percent}%</span>
                      </div>
                      <Progress
                        percent={cat.percent}
                        showInfo={false}
                        strokeColor={catColor(cat.percent)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </Col>
          <Col xs={24} lg={10}>
            <SectionCard title="Upcoming Activities" bodyStyle={{ padding: upcoming.length ? '0 8px' : 0 }}>
              {upcoming.length === 0 ? (
                <EmptyBox text="Nothing upcoming" />
              ) : (
                <List
                  dataSource={upcoming}
                  renderItem={(item) => (
                    <List.Item
                      extra={
                        item.assignee ? (
                          <Avatar size="small" style={{ background: OPS_COLORS.brand }}>
                            {initials(item.assignee.name)}
                          </Avatar>
                        ) : null
                      }
                    >
                      <List.Item.Meta
                        title={
                          <div>
                            <div style={{ color: OPS_COLORS.grey, fontSize: 12 }}>
                              {item.scheduled_date
                                ? moment(item.scheduled_date).format('D MMM')
                                : ''}
                            </div>
                            <span style={{ fontWeight: 600 }}>{item.title}</span>
                          </div>
                        }
                        description={statusTag(item.status, item.is_overdue)}
                      />
                    </List.Item>
                  )}
                />
              )}
            </SectionCard>
          </Col>
        </Row>
      </Spin>
    );
  };

  // --- Timeline: a Gantt of activities grouped by category over the plan year.
  // Recurring occurrences of the same title collapse into one activity row.
  const timelineGroups = useMemo(() => {
    const result = {};
    PLAN_CATEGORIES.forEach((c) => (result[c] = {}));
    let outOfYear = 0;
    items.forEach((item) => {
      const cat = CATEGORY_COLORS[item.category] ? item.category : 'Infrastructure';
      const key = item.title;
      if (!result[cat][key]) {
        result[cat][key] = { title: item.title, byMonth: Array.from({ length: 12 }, () => []), earliest: Infinity };
      }
      const act = result[cat][key];
      if (item.scheduled_date) {
        const m = moment(item.scheduled_date);
        // The track is the 12 months of planYear, so only bucket what actually
        // belongs to it — a series ended by count can run past the plan year,
        // and those would otherwise pile onto the same month as their in-year
        // siblings (inflating bars and corrupting the status roll-up).
        if (m.year() === planYear) {
          act.byMonth[m.month()].push(item);
        } else {
          outOfYear += 1;
        }
        act.earliest = Math.min(act.earliest, m.valueOf());
      }
    });
    // category => sorted activity rows
    const ordered = {};
    PLAN_CATEGORIES.forEach((c) => {
      ordered[c] = Object.values(result[c]).sort((a, b) => a.earliest - b.earliest);
    });
    return { groups: ordered, outOfYear };
  }, [items, planYear]);

  const today = moment();
  const showToday = today.year() === planYear;
  // Position across the 12-month track by month + day + time-of-day, so on e.g.
  // 3 Jul the line sits near the left of the July column and drifts right as the
  // day/month progresses.
  const todayFrac = showToday
    ? (today.month() + (today.date() - 1 + today.hour() / 24) / today.daysInMonth()) / 12
    : 0;
  const FIRST_COL = 240;

  const toggleCategory = (cat) => setCollapsed((c) => ({ ...c, [cat]: !c[cat] }));

  // A month's bar for one activity. A single occurrence opens straight away; a
  // month holding several (e.g. a weekly series) offers a picker, so clicking a
  // bar that covers "12 Mar, 26 Mar" can't silently edit the wrong one.
  const bar = (cat, title, monthItems) => {
    const style = {
      width: '100%',
      height: 20,
      borderRadius: 4,
      cursor: 'pointer',
      ...barFill(cat, monthItems),
    };
    const dates = monthItems.map((it) => moment(it.scheduled_date).format('D MMM')).join(', ');

    if (monthItems.length === 1) {
      return (
        <Tooltip title={`${title} · ${dates}`}>
          <div onClick={() => openEditItem(monthItems[0])} style={style} />
        </Tooltip>
      );
    }

    return (
      <Dropdown
        trigger={['click']}
        overlay={
          <Menu
            onClick={({ key }) => {
              const hit = monthItems.find((it) => String(it.id) === key);
              if (hit) openEditItem(hit);
            }}
          >
            {monthItems.map((it) => (
              <Menu.Item key={String(it.id)}>
                {moment(it.scheduled_date).format('ddd D MMM')}
                {it.is_overdue ? ' · Overdue' : ''}
              </Menu.Item>
            ))}
          </Menu>
        }
      >
        <div title={`${title} · ${dates}`} style={style} />
      </Dropdown>
    );
  };

  const renderTimelineTab = () => {
    if (!selectedPlanId) return noPlanEmpty;
    if (items.length === 0) {
      return <Empty description="No plan items yet" style={{ marginTop: 32 }} />;
    }
    return (
      <Spin spinning={loading}>
        <div style={{ overflowX: 'auto', border: '1px solid #eaecf0', borderRadius: 10, background: '#fff' }}>
          <div style={{ minWidth: 1080, position: 'relative' }}>
            {/* Today marker */}
            {showToday ? (
              <>
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `calc(${FIRST_COL}px + ${todayFrac} * (100% - ${FIRST_COL}px))`,
                    width: 0,
                    borderLeft: '2px solid #111827',
                    zIndex: 2,
                    pointerEvents: 'none',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: 26,
                    left: `calc(${FIRST_COL}px + ${todayFrac} * (100% - ${FIRST_COL}px))`,
                    transform: 'translateX(-50%)',
                    background: OPS_COLORS.brand,
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '1px 8px',
                    borderRadius: 6,
                    zIndex: 3,
                    pointerEvents: 'none',
                  }}
                >
                  Today
                </div>
              </>
            ) : null}

            {/* Month header */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `${FIRST_COL}px repeat(12, 1fr)`,
                borderBottom: '1px solid #eaecf0',
                height: 52,
                alignItems: 'center',
              }}
            >
              <div style={{ padding: '0 12px', fontWeight: 700, color: '#101828' }}>Activity</div>
              {MONTHS.map((m) => (
                <div key={m} style={{ textAlign: 'center', fontWeight: 600, fontSize: 12, color: '#667085' }}>
                  {m}
                </div>
              ))}
            </div>

            {/* Category groups */}
            {PLAN_CATEGORIES.map((cat) => {
              const rows = timelineGroups.groups[cat];
              if (!rows.length) return null;
              const c = categoryColor(cat);
              const isCollapsed = collapsed[cat];
              return (
                <div key={cat}>
                  <div
                    onClick={() => toggleCategory(cat)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 12px',
                      borderBottom: '1px solid #eef0f2',
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: 14,
                      letterSpacing: 0.2,
                      color: '#101828',
                    }}
                  >
                    {isCollapsed ? <RightOutlined style={{ fontSize: 11 }} /> : <DownOutlined style={{ fontSize: 11 }} />}
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: c.dot, display: 'inline-block' }} />
                    {cat}
                  </div>

                  {!isCollapsed
                    ? rows.map((act) => (
                        <div
                          key={act.title}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: `${FIRST_COL}px repeat(12, 1fr)`,
                            borderBottom: '1px solid #f2f4f7',
                          }}
                        >
                          <div
                            style={{
                              padding: '0 12px 0 30px',
                              display: 'flex',
                              alignItems: 'center',
                              fontSize: 13,
                              color: '#344054',
                              minHeight: 44,
                            }}
                          >
                            {act.title}
                          </div>
                          {act.byMonth.map((monthItems, i) => (
                            <div
                              key={i}
                              style={{
                                borderLeft: '1px solid #f4f5f7',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '0 2px',
                              }}
                            >
                              {monthItems.length ? bar(cat, act.title, monthItems) : null}
                            </div>
                          ))}
                        </div>
                      ))
                    : null}
                </div>
              );
            })}
          </div>
        </div>

        {timelineGroups.outOfYear ? (
          <div style={{ fontSize: 12, color: '#b54708', marginTop: 12 }}>
            {timelineGroups.outOfYear} occurrence{timelineGroups.outOfYear === 1 ? '' : 's'} fall
            outside {planYear} and aren&apos;t shown on this track (visible in the Calendar).
          </div>
        ) : null}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12 }}>
          {PLAN_CATEGORIES.map((cat) => {
            const c = categoryColor(cat);
            return (
              <span key={cat} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#667085' }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: c.bar, display: 'inline-block' }} />
                {cat}
              </span>
            );
          })}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#667085' }}>
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                backgroundImage: 'repeating-linear-gradient(45deg, #cbd5e1 0, #cbd5e1 3px, #94a3b8 3px, #94a3b8 6px)',
                display: 'inline-block',
              }}
            />
            In progress
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#667085' }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: '#fff', boxShadow: 'inset 0 0 0 1.5px #ef4444', display: 'inline-block' }} />
            Overdue
          </span>
        </div>
      </Spin>
    );
  };

  // --- Calendar: custom month grid with a category filter + summary rail. -----
  const goMonth = (delta) => setCalValue((v) => v.clone().add(delta, 'month'));

  const toggleFilter = (cat) => {
    setCategoryFilter((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const renderCalendarTab = () => {
    if (!selectedPlanId) return noPlanEmpty;

    const monthAll = items.filter(
      (it) => it.scheduled_date && moment(it.scheduled_date).isSame(calValue, 'month')
    );
    const monthItems = monthAll.filter((it) => categoryFilter.has(it.category));

    const itemsByDate = {};
    monthItems.forEach((it) => {
      const key = moment(it.scheduled_date).format('YYYY-MM-DD');
      (itemsByDate[key] = itemsByDate[key] || []).push(it);
    });

    const total = monthItems.length;
    const overdue = monthItems.filter((i) => i.is_overdue).length;
    const inProgress = monthItems.filter((i) => i.status === 'in_progress').length;
    const done = monthItems.filter((i) => i.status === 'completed').length;
    const remaining = monthItems
      .filter((i) => i.status !== 'completed')
      .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));

    // Weeks (Monday-first) covering the month.
    const gridStart = calValue.clone().startOf('month').startOf('isoWeek');
    const gridEnd = calValue.clone().endOf('month').endOf('isoWeek');
    const days = [];
    let d = gridStart.clone();
    while (d.isSameOrBefore(gridEnd, 'day')) {
      days.push(d.clone());
      d.add(1, 'day');
    }

    const pill = (label, tone) => {
      const tones = {
        grey: { bg: '#f2f4f7', text: '#475467', dot: '#98a2b3' },
        red: { bg: '#fef3f2', text: '#b42318', dot: '#ef4444' },
        green: { bg: '#ecfdf3', text: '#067647', dot: '#12b76a' },
      };
      const t = tones[tone] || tones.grey;
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: t.bg,
            color: t.text,
            fontSize: 12,
            fontWeight: 500,
            padding: '3px 10px',
            borderRadius: 14,
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: t.dot, display: 'inline-block' }} />
          {label}
        </span>
      );
    };

    const dayChip = (it) => {
      const c = it.is_overdue ? OVERDUE_CHIP : categoryColor(it.category);
      const completed = it.status === 'completed';
      return (
        <Tooltip key={it.id} title={`${it.title}${it.scheduled_date ? ` · ${moment(it.scheduled_date).format('D MMM')}` : ''}`}>
          <div
            onClick={(e) => {
              e.stopPropagation();
              openEditItem(it);
            }}
            style={{
              background: c.chipBg,
              color: c.chipText,
              border: `1px solid ${c.chipBorder}`,
              borderRadius: 4,
              padding: '1px 5px',
              marginTop: 3,
              fontSize: 11,
              lineHeight: 1.4,
              cursor: 'pointer',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {completed ? '✓ ' : ''}
            {it.title}
          </div>
        </Tooltip>
      );
    };

    const railCard = (title, body) => (
      <div style={{ border: '1px solid #eaecf0', borderRadius: 10, padding: 14, background: '#fff' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#667085', letterSpacing: 0.4, marginBottom: 10 }}>{title}</div>
        {body}
      </div>
    );

    return (
      <Spin spinning={loading}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
          {/* Calendar */}
          <div style={{ flex: '1 1 640px', minWidth: 0, border: '1px solid #eaecf0', borderRadius: 10, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 20, fontWeight: 700 }}>{calValue.format('MMMM YYYY')}</span>
                <Button size="small" shape="circle" icon={<LeftOutlined />} onClick={() => goMonth(-1)} />
                <Button size="small" shape="circle" icon={<RightOutlined />} onClick={() => goMonth(1)} />
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {pill(`${total} activities`, 'grey')}
                {pill(`${overdue} overdue`, 'red')}
                {pill(`${inProgress} in progress`, 'green')}
                {pill(`${done}/${total} done`, 'grey')}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {WEEKDAYS.map((w) => (
                <div key={w} style={{ textAlign: 'left', padding: '6px 8px', fontSize: 12, fontWeight: 600, color: '#98a2b3', textTransform: 'uppercase' }}>
                  {w}
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', border: '1px solid #eef0f2', borderRadius: 8, overflow: 'hidden' }}>
              {days.map((day) => {
                const inMonth = day.isSame(calValue, 'month');
                const isSelected = day.isSame(calValue, 'day');
                const isToday = day.isSame(today, 'day');
                const dayItems = itemsByDate[day.format('YYYY-MM-DD')] || [];
                return (
                  <div
                    key={day.format('YYYY-MM-DD')}
                    onClick={() => setCalValue(day.clone())}
                    style={{
                      minHeight: 96,
                      borderRight: '1px solid #f2f4f7',
                      borderBottom: '1px solid #f2f4f7',
                      padding: 6,
                      background: isSelected ? '#f6faf7' : inMonth ? '#fff' : '#fafafa',
                      cursor: 'pointer',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        fontWeight: 600,
                        background: isSelected ? OPS_COLORS.brand : 'transparent',
                        border: isToday && !isSelected ? `1px solid ${OPS_COLORS.brand}` : 'none',
                        color: isSelected ? '#fff' : inMonth ? '#101828' : '#cbd5e1',
                      }}
                    >
                      {day.date()}
                    </div>
                    {dayItems.slice(0, 2).map((it) => dayChip(it))}
                    {dayItems.length > 2 ? (
                      <div style={{ fontSize: 11, color: '#667085', marginTop: 2 }}>+{dayItems.length - 2} more</div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Rail */}
          <div style={{ flex: '0 0 300px', maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {railCard(
              'FILTER BY CATEGORY',
              <div>
                {PLAN_CATEGORIES.map((cat) => {
                  const c = categoryColor(cat);
                  const on = categoryFilter.has(cat);
                  const count = monthAll.filter((i) => i.category === cat).length;
                  return (
                    <div
                      key={cat}
                      onClick={() => toggleFilter(cat)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '7px 4px',
                        cursor: 'pointer',
                        opacity: on ? 1 : 0.4,
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: c.dot, display: 'inline-block' }} />
                        {cat}
                      </span>
                      <span style={{ color: '#98a2b3', fontWeight: 600 }}>{count}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {railCard(
              'REMAINING THIS MONTH',
              remaining.length === 0 ? (
                <div style={{ color: '#98a2b3', fontSize: 13 }}>Nothing outstanding 🎉</div>
              ) : (
                <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                  {remaining.map((it) => {
                    const c = it.is_overdue ? OVERDUE_CHIP : categoryColor(it.category);
                    return (
                      <div
                        key={it.id}
                        onClick={() => openEditItem(it)}
                        style={{ display: 'flex', gap: 8, padding: '7px 2px', cursor: 'pointer', borderBottom: '1px solid #f6f7f9' }}
                      >
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.dot, marginTop: 5, flexShrink: 0 }} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#101828', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {it.title}
                          </div>
                          <div style={{ fontSize: 12, color: it.is_overdue ? '#b42318' : '#667085' }}>
                            {moment(it.scheduled_date).format('D MMM')}
                            {it.is_overdue ? ' · overdue' : ''}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}

            {railCard(
              'STATUS KEY',
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12, color: '#475467' }}>
                <span><span style={{ color: '#067647', fontWeight: 700 }}>✓</span> Completed</span>
                <span><span style={{ color: '#12b76a' }}>●</span> In progress</span>
                <span><span style={{ color: '#ef4444' }}>●</span> Overdue (red tile)</span>
                <span><span style={{ color: '#98a2b3' }}>●</span> Scheduled / not yet due</span>
              </div>
            )}
          </div>
        </div>
      </Spin>
    );
  };

  const TABS = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'timeline', label: 'Timeline' },
    { key: 'calendar', label: 'Calendar' },
  ];

  return (
    <LayoutWrapper style={{ display: 'block' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <h1 style={{ margin: 0 }}>Operations Planner</h1>
        </Col>
        <Col>
          <Select
            style={{ width: 240, marginRight: 8 }}
            placeholder="Select a plan"
            value={selectedPlanId || undefined}
            onChange={setSelectedPlanId}
            notFoundContent="No plans"
          >
            {plans.map((plan) => (
              <Option key={plan.id} value={plan.id}>
                {plan.name}
              </Option>
            ))}
          </Select>
          <Button onClick={() => setPlanModalOpen(true)} style={{ marginRight: 8 }}>
            New Plan
          </Button>
          <Button type="primary" disabled={!selectedPlanId} onClick={openNewItem}>
            New Plan Item
          </Button>
        </Col>
      </Row>

      {/* Segmented pill tabs (matches the Figma) */}
      <div style={{ display: 'inline-flex', gap: 8, marginBottom: 20 }}>
        {TABS.map((t) => {
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              style={{
                border: `1px solid ${active ? OPS_COLORS.brand : '#eaecf0'}`,
                background: active ? OPS_COLORS.brand : '#fff',
                color: active ? '#fff' : '#344054',
                fontWeight: 600,
                fontSize: 14,
                padding: '7px 18px',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'dashboard' ? renderDashboardTab() : null}
      {activeTab === 'timeline' ? renderTimelineTab() : null}
      {activeTab === 'calendar' ? renderCalendarTab() : null}

      <Modal
        title={editingItem ? 'Edit Plan Item' : 'New Plan Item'}
        visible={itemModalOpen}
        onCancel={() => {
          setItemModalOpen(false);
          setEditingItem(null);
        }}
        confirmLoading={submitting}
        footer={[
          editingItem ? (
            <Button key="createtask" style={{ float: 'left' }} onClick={() => openTaskForm(editingItem)}>
              Create task
            </Button>
          ) : null,
          editingItem ? (
            <Button key="delete" danger onClick={handleDeleteItem} loading={submitting}>
              Delete
            </Button>
          ) : null,
          <Button
            key="cancel"
            onClick={() => {
              setItemModalOpen(false);
              setEditingItem(null);
            }}
          >
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={submitting} onClick={() => itemForm.submit()}>
            {editingItem ? 'Save' : 'Add'}
          </Button>,
        ]}
      >
        <Form
          form={itemForm}
          layout="vertical"
          initialValues={{ status: 'not_yet_due', repeat: 'none', repeatEnd: 'year' }}
          onFinish={handleSubmitItem}
          onValuesChange={(changed) => {
            if ('repeat' in changed) setRepeat(changed.repeat);
            if ('repeatEnd' in changed) setRepeatEnd(changed.repeatEnd);
          }}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, whitespace: true, message: 'Please enter a title' }]}
          >
            <Input placeholder="e.g. Quarterly cattle drench" />
          </Form.Item>
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Please choose a category' }]}
          >
            <Select placeholder="Select a category">
              {PLAN_CATEGORIES.map((cat) => (
                <Option key={cat} value={cat}>
                  {cat}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="scheduled_date"
            label={
              repeat !== 'none' ? (editingItem ? 'Series start date' : 'Start date') : 'Scheduled date'
            }
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          {(
            <>
              <Form.Item name="repeat" label="Repeat">
                <Select>
                  {REPEAT_OPTIONS.map((o) => (
                    <Option key={o.value} value={o.value}>
                      {o.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              {repeat === 'custom' ? (
                <Form.Item label="Repeat every">
                  <Input.Group compact>
                    <Form.Item name="repeatInterval" noStyle>
                      <InputNumber min={1} max={365} style={{ width: 110 }} />
                    </Form.Item>
                    <Form.Item name="repeatUnit" noStyle>
                      <Select style={{ width: 130 }}>
                        {CUSTOM_UNITS.map((u) => (
                          <Option key={u.value} value={u.value}>
                            {u.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              ) : null}

              {repeat !== 'none' ? (
                <>
                  <Form.Item name="repeatEnd" label="Ends">

                    <Select>
                      <Option value="year">End of {planYear}</Option>
                      <Option value="count">After a set number</Option>
                    </Select>
                  </Form.Item>
                  {repeatEnd === 'count' ? (
                    <Form.Item name="repeatCount" label="Occurrences">
                      <InputNumber min={1} max={366} style={{ width: '100%' }} />
                    </Form.Item>
                  ) : null}
                  <div style={{ fontSize: 12, color: '#667085', marginTop: -8, marginBottom: 12 }}>
                    {editingItem
                      ? 'Updating the recurrence regenerates the upcoming occurrences to this cadence; past and completed ones are kept.'
                      : 'Creates one plan item per occurrence from the start date — weekly repeats on the same weekday, monthly on the same date each month.'}
                  </div>
                </>
              ) : null}
            </>
          )}

          <Form.Item name="status" label="Status">
            <Select>
              {PLAN_ITEM_STATUSES.map((s) => (
                <Option key={s.value} value={s.value}>
                  {s.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} />
          </Form.Item>

          {/* Advanced (maintenance-style) fields — hidden behind a toggle. */}
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8, marginBottom: advancedOpen ? 8 : 0 }}>
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => {
                const next = !advancedOpen;
                setAdvancedOpen(next);
                if (next) ensureAdvancedData();
              }}
            >
              {advancedOpen ? <DownOutlined /> : <RightOutlined />} Advanced
            </Button>
          </div>

          {advancedOpen ? (
            <div>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="enterprise" label="Enterprise">
                    <Select allowClear placeholder="Select" showSearch optionFilterProp="children">
                      {ENTERPRISES.map((e) => (
                        <Option key={e} value={e}>
                          {e}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="task_type" label="Task type">
                    <Select allowClear placeholder="Select">
                      {TASK_TYPES.map((t) => (
                        <Option key={t} value={t}>
                          {t}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="primary_object_id" label="Location">
                    <Select allowClear showSearch optionFilterProp="children" placeholder="Select an area">
                      {planAreas.map((a) => (
                        <Option key={a.id} value={a.id}>
                          {a.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="asset_id" label="Asset">
                    <Select allowClear showSearch optionFilterProp="children" placeholder="Select an asset">
                      {planAssets.map((a) => (
                        <Option key={a.id} value={a.id}>
                          {a.title}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="responsible_user_id" label="Responsible person">
                    <Select allowClear showSearch optionFilterProp="children" placeholder="Select a user">
                      {planUsers.map((u) => (
                        <Option key={u.id} value={u.id}>
                          {u.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="risk_level" label="Risk level">
                    <Select allowClear placeholder="Select">
                      {RISK_LEVELS.map((r) => (
                        <Option key={r.value} value={r.value}>
                          {r.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="evidence_required" label="Evidence required">
                <TextArea rows={2} placeholder="e.g. Service record and photograph" />
              </Form.Item>
            </div>
          ) : null}
        </Form>
      </Modal>

      <Modal
        title="New Plan"
        visible={planModalOpen}
        onCancel={() => setPlanModalOpen(false)}
        onOk={() => planForm.submit()}
        confirmLoading={submitting}
        okText="Create"
      >
        <Form
          form={planForm}
          layout="vertical"
          initialValues={{ year: moment().year() }}
          onFinish={handleCreatePlan}
        >
          <Form.Item
            name="name"
            label="Plan name"
            rules={[{ required: true, whitespace: true, message: 'Please enter a plan name' }]}
          >
            <Input placeholder="e.g. Springfield 2026 Annual Plan" />
          </Form.Item>
          <Form.Item
            name="year"
            label="Year"
            rules={[{ required: true, message: 'Please enter a year' }]}
          >
            <InputNumber style={{ width: '100%' }} min={2000} max={2100} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Real "New Task" form, pre-filled from a plan item — the user reviews
          and creates the task manually. Mounted only while open: NewTask fetches
          users/assets/forms on mount, which the Planner shouldn't pay for until
          the form is actually needed. */}
      {taskFormOpen ? (
        <NewTask
          visibleNewTaskModal={taskFormOpen}
          setVisibleNewTaskModal={setTaskFormOpen}
          propertyId={activePropertyId}
          primaryObjects={taskAreas}
          task={taskPrefill}
          onSaved={() => {
            setTaskFormOpen(false);
            loadDashboard(selectedPlanId);
          }}
          kanbanToggle={-1}
        />
      ) : null}
    </LayoutWrapper>
  );
};

export default Planner;
